import { Alert, Button, Modal, Radio, Typography } from "antd"
import type { RadioChangeEvent } from "antd"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { ComparisonTable } from "../../DocumentSync/ComparisonTable.tsx"
import { mapFieldComparisonToGroupedValues } from "../../DocumentSync/grouped-comparison-adapter.ts"
import {
  buildRecommendedSourceEntries,
  formatColumnSublabel,
  formatSingleRecommendedSource,
  formatSourceLabel
} from "../../DocumentSync/recommended-source-summary.ts"
import type {
  DocumentLabelInfo,
  FieldComparison,
  RecommendedSourceSummary,
  SourceType
} from "../../DocumentSync/types.ts"

const { Text, Title } = Typography

type ConfirmMode = "recommended" | "lead"

const SOURCE_COLUMNS: { key: SourceType; labelKey: string }[] = [
  { key: "lead", labelKey: "leadColumn" },
  { key: "proposal", labelKey: "proposalColumn" },
  { key: "contract", labelKey: "contractColumn" }
]

/** Wide enough for a Field column plus three readable value columns at a
 * normal desktop zoom; the viewport caps it on anything narrower. */
const MODAL_WIDTH = 1160

interface ConfirmProjectInformationModalProps {
  open: boolean
  diffs: FieldComparison[]
  onCancel: () => void
  onConfirm: (applyFields?: Record<string, unknown>) => void
  confirmLoading?: boolean
  recommendedSourceSummary?: RecommendedSourceSummary
  documentLabels?: {
    proposal?: DocumentLabelInfo
    contract?: DocumentLabelInfo
  }
  /**
   * Already-translated reasons this Lead cannot become a Project yet.
   * Non-empty disables Confirm and says why. Differences between the
   * documents are never one of these — resolving those is what this modal
   * is for.
   */
  blockingIssues?: string[]
}

/**
 * Shown when converting a Lead into a Project and the sync-preview endpoint
 * reports differences between the Lead, Proposal and Contract. Two choices —
 * the recommended (status-based) values, or the Lead's own, an equally
 * valid pick and never a fallback — applied to every changed field at
 * once; no per-field selection. Never mounted when there are no diffs —
 * that case proceeds straight through with no UI.
 *
 * The comparison table renders every field the sync-preview response
 * carries (not only the ones that differ), so a user can see the full
 * picture of what the new Project will inherit. Emphasis is per cell, from
 * the backend's own `proposal_differs`/`contract_differs`: a row where the
 * Proposal matches the Lead and only the Contract drifted highlights the
 * Contract cell alone, rather than painting all three as if they
 * disagreed.
 *
 * Layout is a fixed shell around one scroll region: the title, the
 * recommendation choice and the actions all stay put, and only the
 * comparison scrolls — with its header row pinned, so a Lead with fifty
 * milestones is still readable without losing track of which column is
 * which.
 */
function ConfirmProjectInformationModal({
  open,
  diffs,
  onCancel,
  onConfirm,
  confirmLoading,
  recommendedSourceSummary,
  documentLabels,
  blockingIssues
}: ConfirmProjectInformationModalProps) {
  const { t } = useTranslation()
  const [mode, setMode] = useState<ConfirmMode>("recommended")

  const changedDiffs = useMemo(
    () => diffs.filter((diff) => diff.has_diff),
    [diffs]
  )

  useEffect(() => {
    if (!open) return
    setMode("recommended")
  }, [open])

  const isBlocked = !!blockingIssues?.length

  const handleConfirm = () => {
    if (isBlocked) return
    if (mode === "lead") {
      onConfirm(undefined)
      return
    }

    onConfirm(
      Object.fromEntries(
        changedDiffs.map((diff) => [diff.key, diff.recommended_value])
      )
    )
  }

  const notSetLabel = t("leadApproval.confirmProjectInfo.notSet")

  const tableColumns = SOURCE_COLUMNS.map((column) => ({
    key: column.key,
    label: t(`leadApproval.confirmProjectInfo.${column.labelKey}`),
    sublabel:
      column.key === "lead"
        ? undefined
        : formatColumnSublabel(
            column.key === "proposal"
              ? documentLabels?.proposal
              : documentLabels?.contract
          )
  }))

  const sourceEntries = useMemo(
    () => buildRecommendedSourceEntries(diffs),
    [diffs]
  )
  const singleSourceLine = formatSingleRecommendedSource(
    sourceEntries,
    recommendedSourceSummary,
    documentLabels,
    t
  )

  const tableRows = diffs.map((diff) => {
    // Three columns share one row here, so there is no single "baseline vs
    // document being generated" pair the way the two Generate modals have —
    // `scope_diff`/`payment_terms_diff` can only ever describe one
    // direction. Attach it to whichever non-Lead column the backend itself
    // already recommends (computed from this same comparisons snapshot);
    // when Lead is the recommendation, no column gets the diff and the cell
    // still renders its collapsed summary + "View all", just without a
    // "View changes" affordance.
    const diffColumnKey =
      diff.recommended_source !== "lead" ? diff.recommended_source : undefined
    const grouped = mapFieldComparisonToGroupedValues(
      diff,
      ["lead", "proposal", "contract"],
      t,
      diffColumnKey
    )

    // Absent on an older cached response — fall back to the whole-row
    // signal rather than silently highlighting nothing.
    const perCellKnown =
      diff.proposal_differs !== undefined || diff.contract_differs !== undefined
    const diffColumns = perCellKnown
      ? [
          ...(diff.proposal_differs ? ["proposal"] : []),
          ...(diff.contract_differs ? ["contract"] : [])
        ]
      : undefined

    return {
      key: diff.key,
      label: diff.label,
      hasDiff: diff.has_diff,
      diffColumns,
      values: {
        lead: diff.lead_display || notSetLabel,
        proposal: diff.proposal_display || notSetLabel,
        contract: diff.contract_display || notSetLabel
      },
      cellNotes: diff.contract_locked
        ? { contract: t("leadApproval.confirmProjectInfo.locked") }
        : undefined,
      groupedValues: grouped?.groupedValues,
      groupedCellCopy: grouped?.groupedCellCopy
    }
  })

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      maskClosable={false}
      centered
      width={MODAL_WIDTH}
      style={{ maxWidth: "calc(100vw - 32px)" }}
      styles={{
        content: {
          display: "flex",
          flexDirection: "column",
          maxHeight: "calc(100vh - 48px)",
          padding: 0
        },
        header: { padding: "20px 24px 0", marginBottom: 0 },
        // The only scroll region in the modal is the comparison table
        // inside this body — the question, the choice and the actions stay
        // where the user left them however long the comparison gets.
        body: {
          display: "flex",
          flexDirection: "column",
          gap: 16,
          flex: "1 1 auto",
          minHeight: 0,
          overflow: "hidden",
          padding: "16px 24px"
        },
        footer: {
          margin: 0,
          padding: "12px 24px 20px",
          borderTop: "1px solid #f0f0f0"
        }
      }}
      title={
        <Title level={4} style={{ margin: 0 }}>
          {t("leadApproval.confirmProjectInfo.title")}
        </Title>
      }
      footer={
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <Text
            type="secondary"
            className="text-xs"
            data-testid="confirm-selection-summary"
          >
            {mode === "recommended"
              ? t("leadApproval.confirmProjectInfo.selectedRecommended")
              : t("leadApproval.confirmProjectInfo.selectedLead")}
          </Text>
          <div className="flex gap-2 ml-auto">
            <Button onClick={onCancel} disabled={confirmLoading}>
              {t("leadApproval.confirmProjectInfo.cancel")}
            </Button>
            <Button
              type="primary"
              loading={confirmLoading}
              disabled={isBlocked}
              onClick={handleConfirm}
            >
              {t("leadApproval.confirmProjectInfo.confirm")}
            </Button>
          </div>
        </div>
      }
    >
      <div className="flex items-baseline gap-2 flex-wrap shrink-0">
        <Text type="secondary">
          {t("leadApproval.confirmProjectInfo.description")}
        </Text>
        <Text strong className="text-xs" data-testid="confirm-diff-count">
          {changedDiffs.length
            ? t("leadApproval.confirmProjectInfo.differenceCount", {
                count: changedDiffs.length
              })
            : t("leadApproval.confirmProjectInfo.noDifferences")}
        </Text>
      </div>

      {/* A flex chain all the way down, never a percentage height: the
          modal is capped by max-height, which gives a child nothing
          definite to take 100% of, so the table would grow past its slot
          and paint over the choice below instead of scrolling. */}
      <div className="flex-1 min-h-0 flex flex-col">
        <ComparisonTable
          columns={tableColumns}
          rows={tableRows}
          testIdPrefix="confirm-diff"
          fieldColumnLabel={t("leadApproval.confirmProjectInfo.fieldColumn")}
          scrollable
          changesLayout="row"
          changesTitle={(column) =>
            t("leadApproval.confirmProjectInfo.changesComparedWithLead", {
              source: column.sublabel
                ? `${column.label} · ${column.sublabel}`
                : column.label
            })
          }
        />
      </div>

      {isBlocked && (
        <Alert
          type="error"
          showIcon
          className="shrink-0"
          data-testid="confirm-blocking-issues"
          message={t("leadApproval.confirmProjectInfo.blockingTitle")}
          description={
            <ul className="list-disc pl-4 m-0">
              {blockingIssues!.map((issue) => (
                <li key={issue}>{issue}</li>
              ))}
            </ul>
          }
        />
      )}

      <div className="shrink-0">
        <Text className="block mb-2">
          {t("leadApproval.confirmProjectInfo.question")}
        </Text>

        <Radio.Group
          className="flex flex-col gap-3"
          value={mode}
          onChange={(e: RadioChangeEvent) =>
            setMode(e.target.value as ConfirmMode)
          }
        >
          <Radio
            value="recommended"
            className="items-start [&_.ant-radio]:self-start [&_.ant-radio]:mt-1"
          >
            <div>
              <div>{t("leadApproval.confirmProjectInfo.useRecommended")}</div>
              {singleSourceLine ? (
                <Text
                  type="secondary"
                  className="text-xs"
                  data-testid="confirm-recommended-source"
                >
                  {singleSourceLine}
                </Text>
              ) : (
                <div data-testid="confirm-recommended-source">
                  <Text type="secondary" className="text-xs block">
                    {t(
                      "leadApproval.confirmProjectInfo.recommendedMixedSources"
                    )}
                  </Text>
                  <ul className="list-disc pl-4 m-0">
                    {sourceEntries.map((entry) => (
                      <li key={entry.source}>
                        <Text type="secondary" className="text-xs">
                          {t(
                            "leadApproval.confirmProjectInfo.recommendedSourceFields",
                            {
                              source: formatSourceLabel(
                                entry.source,
                                documentLabels,
                                t
                              ),
                              fields: entry.fieldLabels.join(", ")
                            }
                          )}
                        </Text>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </Radio>
          <Radio
            value="lead"
            className="items-start [&_.ant-radio]:self-start [&_.ant-radio]:mt-1"
          >
            <div>
              <div>{t("leadApproval.confirmProjectInfo.useLead")}</div>
              <Text type="secondary" className="text-xs">
                {t("leadApproval.confirmProjectInfo.useLeadHelper")}
              </Text>
            </div>
          </Radio>
        </Radio.Group>
      </div>
    </Modal>
  )
}

export { ConfirmProjectInformationModal }
